import {Component, DestroyRef, inject, OnInit} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {ActivatedRoute, Router} from '@angular/router';
import {DatePipe, Location} from '@angular/common';
import {FormsModule} from '@angular/forms';
import {TranslatePipe, TranslateService} from '@ngx-translate/core';
import {catchError, forkJoin, of} from 'rxjs';
import {MatProgressSpinner} from '@angular/material/progress-spinner';
import {MatIcon} from '@angular/material/icon';
import {MatButton} from '@angular/material/button';
import {BenchOverviewService} from '../../../services/bench-overview.service';
import {BenchEntryDTO, BenchFirm, BusinessUnitDTO} from '../../../shared/model/bench-entry.model';
import {MessageService} from '../../../shared/components/message.service';
import {DateService} from '../../../services/date.service';
import {CompanyService} from '../../../services/company.service';
import {MeetingRowComponent} from './meeting-row.component';
import {CompanyBadgeComponent} from '../../../shared/components/company-badge/company-badge.component';
import {CiSelectComponent} from '../../../../profiler-theme';
import {MeetingFilterStateService} from '../../../services/meeting-filter-state.service';
import {pathOf, replaceQueryParams} from '../../../shared/replace-query-params';

/**
 * Meeting-Modus: walk through the bench firm by firm and capture each consultant's
 * current state inline — no detail page. A dedicated route (not a modal).
 */
@Component({
  selector: 'app-meeting-mode',
  imports: [
    DatePipe,
    FormsModule,
    TranslatePipe,
    MatProgressSpinner,
    MatIcon,
    MatButton,
    MeetingRowComponent,
    CompanyBadgeComponent,
    CiSelectComponent,
  ],
  templateUrl: './meeting-mode.component.html',
  styleUrl: './meeting-mode.component.scss',
  standalone: true,
})
export class MeetingModeComponent implements OnInit {
  private readonly benchService = inject(BenchOverviewService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly router = inject(Router);
  private readonly messageService = inject(MessageService);
  private readonly dateService = inject(DateService);
  private readonly companyService = inject(CompanyService);
  private readonly translate = inject(TranslateService);
  private readonly route = inject(ActivatedRoute);
  private readonly location = inject(Location);
  private readonly filterState = inject(MeetingFilterStateService);
  private ownPath = '';

  entries: BenchEntryDTO[] = [];
  businessUnits: BusinessUnitDTO[] = [];
  isLoading = true;

  onlyOpenOffers = true;
  buFilter = 'ALL';
  buFilterOptions: string[] = [];
  private buFilterLabels = new Map<string, string>();

  displayBuFilter = (value: string): string => this.buFilterLabels.get(value) ?? value;

  /** Fallback-Bucket-Key für Einträge ohne auflösbare Firma — sie dürfen nie unsichtbar werden (NP20-894). */
  readonly NO_FIRM = '';

  // Derived view state, recomputed only when data/filters change (not on every CD tick).
  visibleEntries: BenchEntryDTO[] = [];
  meetingCount = 0;
  /** Firmengruppen in Anzeigereihenfolge: Katalog zuerst, dann übrige, NO_FIRM zuletzt. */
  orderedFirms: BenchFirm[] = [];
  private byFirm = new Map<BenchFirm, BenchEntryDTO[]>();

  private readonly collapsed = new Set<BenchFirm>();

  /** Computed fresh so a meeting left open past midnight doesn't compare against a stale date. */
  get today(): string {
    return this.dateService.toLocalDateString(new Date()) as string;
  }

  ngOnInit(): void {
    this.ownPath = pathOf(this.location.path(true));
    this.restoreFilterState();
    this.buildFilterOptions();
    this.translate.onLangChange
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.buildFilterOptions());
    // NP20-894: Firmen als Teil des forkJoin laden (kein Race) und entry.firm über
    // resolveAbbreviation normalisieren — analog bench-overview. Sonst fallen Einträge mit
    // companyId-/abweichend geschriebener Firma aus allen Gruppen und werden unsichtbar.
    forkJoin({
      companies: this.companyService.load(),
      entries: this.benchService.getEntries(),
      businessUnits: this.benchService.getBusinessUnits().pipe(catchError(() => of([] as BusinessUnitDTO[]))),
    })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ({entries, businessUnits}) => {
          const normalize = (firm: BenchFirm | null | undefined): BenchFirm =>
            this.companyService.resolveAbbreviation(firm) ?? (firm ?? '');
          this.entries = entries.map(e => ({...e, firm: normalize(e.firm)}));
          this.businessUnits = businessUnits;
          this.normalizeBuFilter();
          this.buildFilterOptions();
          this.applyFilters();
          this.isLoading = false;
        },
        error: () => {
          this.isLoading = false;
          this.messageService.error('benchOverview.loadError');
        },
      });
  }

  onFilterChange(): void {
    this.persistFilterState();
    this.applyFilters();
  }

  /** Die Adresszeile gewinnt, sonst greift die gemerkte Auswahl (NP20-984). */
  private restoreFilterState(): void {
    const params = this.route.snapshot.queryParamMap;
    const state = this.filterState.hasFilterParams(params)
      ? this.filterState.fromQueryParams(params)
      : this.filterState.read();
    this.buFilter = state.bu;
    this.onlyOpenOffers = state.onlyOpen;
    this.persistFilterState();
  }

  private persistFilterState(): void {
    const state = {bu: this.buFilter, onlyOpen: this.onlyOpenOffers};
    this.filterState.write(state);
    replaceQueryParams(this.location, this.router, this.ownPath, this.filterState.toQueryParams(state));
  }

  /** Eine veraltete BU-Id fällt auf ALL — nur wenn die BUs wirklich geladen sind. */
  private normalizeBuFilter(): void {
    if (this.buFilter === 'ALL' || !this.businessUnits.length) return;
    if (this.businessUnits.some(bu => bu.id === this.buFilter)) return;
    this.buFilter = 'ALL';
    this.persistFilterState();
  }

  private buildFilterOptions(): void {
    this.buFilterOptions = ['ALL', ...this.businessUnits.map(bu => bu.id)];
    const labels = new Map<string, string>();
    labels.set('ALL', this.translate.instant('meetingMode.allBus'));
    for (const bu of this.businessUnits) {
      labels.set(bu.id, `${bu.key} — ${bu.name}`);
    }
    this.buFilterLabels = labels;
  }

  entriesByFirm(firm: BenchFirm): BenchEntryDTO[] {
    return this.byFirm.get(firm) ?? [];
  }

  private applyFilters(): void {
    this.visibleEntries = this.entries.filter(e => {
      if (this.onlyOpenOffers && !e.offers.some(o => o.status === 'OPEN' || o.status === 'FEEDBACK')) return false;
      if (this.buFilter !== 'ALL' && !(e.offers ?? []).some(o => o.businessUnitId === this.buFilter)) return false;
      return true;
    });
    // NP20-894: Nach der (normalisierten) Firma des Eintrags gruppieren statt nach dem Katalog,
    // damit jeder sichtbare Eintrag in genau einer Gruppe landet — Zähler == angezeigte Einträge.
    const groups = new Map<BenchFirm, BenchEntryDTO[]>();
    for (const e of this.visibleEntries) {
      const key = (e.firm ?? '').trim() || this.NO_FIRM;
      const bucket = groups.get(key);
      if (bucket) bucket.push(e);
      else groups.set(key, [e]);
    }
    this.byFirm = groups;

    // Anzeigereihenfolge: bekannte Firmen in Katalogreihenfolge, dann übrige alphabetisch,
    // die Fallback-Gruppe ohne Firma zuletzt.
    const catalog = this.companyService.companies().map(c => c.abbreviation);
    const present = new Set(groups.keys());
    const known = catalog.filter(f => present.has(f));
    const extras = [...present]
      .filter(f => f !== this.NO_FIRM && !catalog.includes(f))
      .sort((a, b) => a.localeCompare(b));
    this.orderedFirms = present.has(this.NO_FIRM) ? [...known, ...extras, this.NO_FIRM] : [...known, ...extras];

    this.meetingCount = this.visibleEntries.length;
  }

  isCollapsed(firm: BenchFirm): boolean {
    return this.collapsed.has(firm);
  }

  toggleFirm(firm: BenchFirm): void {
    if (this.collapsed.has(firm)) {
      this.collapsed.delete(firm);
    } else {
      this.collapsed.add(firm);
    }
  }

  toggleOnlyOpen(): void {
    this.onlyOpenOffers = !this.onlyOpenOffers;
    this.persistFilterState();
    this.applyFilters();
  }

  backToList(): void {
    this.router.navigate(['/berater-abfrage']);
  }
}
